import React, { useState } from 'react';
import { Mail, Phone, MessageCircle, Send, Loader2, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../i18n/LanguageContext';
import { Reveal } from '../components/Reveal';

export default function Contact() {
  const { t } = useLanguage();
  const c = t.contact;
  const info = t.contactInfo;

  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch('/contact-api/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...form,
          language: document.documentElement.lang || 'pt',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || 'Não foi possível enviar sua mensagem.'
        );
      }

      toast.success(c.success);

      setForm({
        name: '',
        email: '',
        company: '',
        message: '',
      });
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);

      toast.error(
        error.message ||
          c.error ||
          'Não foi possível enviar sua mensagem. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: 'transparent',
    borderBottom: '1px solid var(--rule-strong)',
    color: 'var(--ink)',
  };

  const inputClass =
    'w-full py-3 text-[15px] focus:outline-none transition-colors placeholder:italic';

  const channels = [
    { icon: Mail, label: c.labelEmail, value: info.email, href: `mailto:${info.email}`, testid: 'contact-email', external: false },
    { icon: Phone, label: c.labelPhone, value: info.phone, href: `tel:${info.phone}`, testid: 'contact-phone', external: false },
    {
      icon: MessageCircle,
      label: c.labelWhatsapp,
      value: info.phone,
      href: `https://wa.me/${info.whatsapp}`,
      testid: 'contact-whatsapp',
      external: true,
    },
  ];

  return (
    <div data-testid="page-contact">
      {/* Header */}
      <section className="px-6 lg:px-12 pt-32 md:pt-40 pb-16">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-2">
            <Reveal>
              <p className="overline">{c.overline}</p>
            </Reveal>
          </div>
          <div className="md:col-span-10">
            <Reveal delay={0.05}>
              <h1
                className="font-serif font-light text-[2.6rem] md:text-[5.4rem] leading-[1] tracking-tight"
                style={{ color: 'var(--ink)' }}
              >
                {c.title}
              </h1>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-8 text-[17px] leading-relaxed max-w-2xl" style={{ color: 'var(--ink-2)' }}>
                {c.lead}
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-10 text-2xl md:text-3xl font-serif italic">
                <a href={`mailto:${info.email}`} className="link-underline" style={{ color: 'var(--ink)' }}>
                  {info.email}
                </a>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Form + channels */}
      <section
        className="px-6 lg:px-12 py-24"
        style={{ background: 'var(--paper-2)', borderTop: '1px solid var(--rule)' }}
      >
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-16">
          {/* Form */}
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="font-serif text-2xl md:text-3xl font-light mb-10" style={{ color: 'var(--ink)' }}>
                {c.formTitle}
              </h2>
              <form onSubmit={onSubmit} className="space-y-8" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-8">
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder={c.name}
                    required
                    disabled={loading}
                    className={inputClass}
                    style={inputStyle}
                    data-testid="contact-input-name"
                  />
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder={c.email}
                    required
                    disabled={loading}
                    className={inputClass}
                    style={inputStyle}
                    data-testid="contact-input-email"
                  />
                </div>
                <input
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  placeholder={c.company}
                  disabled={loading}
                  className={inputClass}
                  style={inputStyle}
                  data-testid="contact-input-company"
                />
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  placeholder={c.message}
                  required
                  rows={5}
                  disabled={loading}
                  className={`${inputClass} resize-none`}
                  style={inputStyle}
                  data-testid="contact-input-message"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="btn-solid disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      {c.sending}
                    </>
                  ) : (
                    <>
                      {c.send}
                      <Send size={14} />
                    </>
                  )}
                </button>
              </form>
            </Reveal>
          </div>

          {/* Channels */}
          <div className="md:col-span-5 md:pl-12" style={{ borderLeft: '1px solid var(--rule)' }}>
            <Reveal delay={0.1}>
              <div className="space-y-10">
                {channels.map((ch) => {
                  const Icon = ch.icon;
                  return (
                    <a
                      key={ch.label}
                      href={ch.href}
                      target={ch.external ? '_blank' : undefined}
                      rel="noopener noreferrer"
                      className="group flex items-start gap-5"
                      data-testid={ch.testid}
                    >
                      <div
                        className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center transition-colors"
                        style={{ border: '1px solid var(--rule-strong)', color: 'var(--ink)' }}
                      >
                        <Icon size={16} />
                      </div>
                      <div>
                        <p className="overline">{ch.label}</p>
                        <p className="mt-1 font-serif text-xl" style={{ color: 'var(--ink)' }}>
                          {ch.value}
                        </p>
                      </div>
                    </a>
                  );
                })}

                <a
                  href={`https://wa.me/${info.whatsapp}?text=${encodeURIComponent(c.whatsappText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-cta"
                  className="link-underline text-[13px] mt-4"
                >
                  {c.whatsappCta}
                  <ArrowUpRight size={14} />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
